import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Create as EditIcon,
  ArrowForward as ArrowIcon,
  TextFields as TextIcon
} from '@mui/icons-material';
import CompareIcon from '@mui/icons-material/CompareArrows';
import { generateTimecode } from '../../utils/vttUtils';
import { formatVttTime } from '../../utils/vttUtils';

/**
 * Component for displaying differences between two VTT files
 */
const VttDiffViewer = ({ diff, isLoading, error, olderVersion, newerVersion, formatTime }) => {

  // Add this function to highlight text differences
  const renderTextWithHighlights = (text, isOld, item) => {
    // If not a text change, just return the text normally
    if (item.type !== 'changed' || !item.changes?.text) {
      return text;
    }
    
    // For simple display, highlight the entire text
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          whiteSpace: 'pre-wrap',
          backgroundColor: isOld ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)',
          padding: '4px 8px',
          borderRadius: '4px',
          border: '1px solid',
          borderColor: isOld ? 'rgba(244, 67, 54, 0.3)' : 'rgba(76, 175, 80, 0.3)',
        }}
      >
        {text}
      </Typography>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: 4
      }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading subtitle content...
          </Typography>
        </Stack>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (!diff || diff.length === 0 || !olderVersion || !newerVersion) {
    return (
      <Alert severity="info" icon={<CompareIcon />} sx={{ mb: 2 }}>
        No differences found between these versions or versions not properly loaded.
      </Alert>
    );
  }
  
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Box sx={{ flex: 1, px: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Version {olderVersion.versionNumber} ({formatTime(olderVersion.createdAt).relative})
          </Typography>
        </Box>
        <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
          <ArrowIcon color="action" />
        </Box>
        <Box sx={{ flex: 1, px: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Version {newerVersion.versionNumber} ({formatTime(newerVersion.createdAt).relative})
          </Typography>
        </Box>
      </Box>
      
      {diff.map((item) => {
        // Skip unchanged items to focus on differences
        if (item.type === 'unchanged') {
          return null;
        }
        
        return (
          <Paper 
            key={item.id}
            variant="outlined"
            sx={{ 
              mb: 2,
              borderRadius: '8px',
              borderColor: item.type === 'added' ? 'success.light' : 
                          item.type === 'removed' ? 'error.light' : 
                          'warning.light',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              px: 2, 
              py: 1,
              backgroundColor: item.type === 'added' ? 'rgba(76, 175, 80, 0.08)' : 
                              item.type === 'removed' ? 'rgba(244, 67, 54, 0.08)' : 
                              'rgba(255, 152, 0, 0.08)',
              borderBottom: '1px solid',
              borderColor: item.type === 'added' ? 'success.light' : 
                          item.type === 'removed' ? 'error.light' : 
                          'warning.light',
            }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {item.type === 'added' && <AddIcon fontSize="small" color="success" />}
                {item.type === 'removed' && <RemoveIcon fontSize="small" color="error" />}
                {item.type === 'changed' && <EditIcon fontSize="small" color="warning" />}
                
                <Typography variant="caption" fontWeight={600}>
                  {item.type === 'added' ? 'Added Subtitle' : 
                   item.type === 'removed' ? 'Removed Subtitle' : 
                   'Modified Subtitle'}
                </Typography>
                
                <Chip 
                  size="small" 
                  label={`#${item.id}`}
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.625rem' } }}
                />
                
                {item.type === 'changed' && item.changes?.timing && (
                  <Chip 
                    size="small" 
                    label="Timing Changed"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.625rem' } }}
                  />
                )}

                {item.type === 'changed' && item.changes?.text && (
                  <Chip 
                    size="small" 
                    label="Text Changed"
                    color="info"
                    icon={<TextIcon sx={{ fontSize: '0.75rem !important' }} />}
                    variant="outlined"
                    sx={{ 
                      height: 20, 
                      '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.625rem' },
                      backgroundColor: 'rgba(33, 150, 243, 0.1)'
                    }}
                  />
                )}
              </Stack>
            </Box>
            
            <Box sx={{ display: 'flex' }}>
              {/* Left side (old version) */}
              <Box sx={{ 
                flex: 1, 
                p: 2, 
                backgroundColor: item.type === 'added' ? 'rgba(0,0,0,0.02)' : 'white',
                borderRight: '1px dashed',
                borderColor: 'divider'
              }}>
                {item.type !== 'added' && item.oldItem && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {item.oldItem.timecode || generateTimecode(item.oldItem)}
                    </Typography>
                    
                    {/* Use different styling for text changes */}
                    {item.type === 'changed' && item.changes?.text ? (
                      <Box sx={{ position: 'relative' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            color: 'error.dark',
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            border: '1px solid',
                            borderColor: 'error.light',
                            borderRadius: '4px',
                            padding: '6px',
                          }}
                        >
                          {item.oldItem.text.join('\n')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 8, 
                            backgroundColor: 'error.light',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: '4px',
                            fontSize: '0.625rem',
                            fontWeight: 500
                          }}
                        >
                          Old Text
                        </Typography>
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          ...(item.type === 'removed' && { 
                            textDecoration: 'line-through', 
                            color: 'error.main' 
                          })
                        }}
                      >
                        {item.oldItem.text.join('\n')}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              
              {/* Arrow/separator */}
              <Box sx={{ 
                width: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.02)'
              }}>
                <ArrowIcon fontSize="small" color="action" />
              </Box>
              
              {/* Right side (new version) */}
              <Box sx={{ 
                flex: 1, 
                p: 2,
                backgroundColor: item.type === 'removed' ? 'rgba(0,0,0,0.02)' : 'white' 
              }}>
                {item.type !== 'removed' && item.newItem && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {item.newItem.timecode || generateTimecode(item.newItem)}
                    </Typography>
                    
                    {/* Use different styling for text changes */}
                    {item.type === 'changed' && item.changes?.text ? (
                      <Box sx={{ position: 'relative' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            color: 'success.dark',
                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                            border: '1px solid',
                            borderColor: 'success.light',
                            borderRadius: '4px',
                            padding: '6px',
                          }}
                        >
                          {item.newItem.text.join('\n')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 8, 
                            backgroundColor: 'success.light',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: '4px',
                            fontSize: '0.625rem',
                            fontWeight: 500
                          }}
                        >
                          New Text
                        </Typography>
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          ...(item.type === 'added' && { color: 'success.main' })
                        }}
                      >
                        {item.newItem.text.join('\n')}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
      
      {diff.filter(item => item.type !== 'unchanged').length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          These versions appear to be identical. No differences were found.
        </Alert>
      )}
    </Box>
  );
};

export default VttDiffViewer;